/* sk-klaviyo-consent.js - load Klaviyo's onsite tracking only when the shopper allows it.
 *
 * WHY THIS EXISTS. Klaviyo's Shopify integration offers no consent setting: its app embed
 * injects static.klaviyo.com/onsite/js unconditionally on every page, so it profiled EU and UK
 * shoppers who had pressed Decline. Counsel flagged this; there is no toggle to fix it.
 *
 * The fix is the same shape as the GA4 bridge in sk-consent-bridge.js: the app embed block is
 * DISABLED in config/settings_data.json, and this file injects the script itself, but only
 * after checking the shopper's answer. If this file is removed, the app embed must be
 * re-enabled or Klaviyo onsite tracking stops entirely.
 *
 * It does NOT gate the newsletter signup. That form posts to Shopify (form_type=customer) and
 * reaches Klaviyo server-side, so signups keep working with tracking switched off - which is
 * the correct split: consenting to be emailed is not consenting to be followed around the site.
 *
 * WORK ITEM Q18 (2026-09-29 default decision, workstreams/analytics/Launch Readiness Plan.md):
 * Cookiebot is now the consent surface of record on store.stonekite.com, not Shopify's own
 * native customer-privacy banner. This file used to gate on Shopify's native banner signal
 * (`visitorConsentCollected`, `Shopify.customerPrivacy`) plus a manual EEA/UK/CH region list
 * standing in for Shopify's narrower banner configuration (EU27 + UK only, missing Iceland,
 * Liechtenstein, Norway). Cookiebot's own domain-group configuration (id
 * a2477667-a678-45fe-87f1-69a643b8be55, workstreams/analytics/Launch Readiness Plan.md row Q7)
 * is now what decides which regions see a banner at all, so that region list and the
 * Shopify-banner fallback are retired here rather than duplicated: this file trusts whatever
 * `Cookiebot.consent.marketing` reads once Cookiebot says it is ready, and nothing else.
 *
 * Consent is read from Cookiebot's own events and consent object (CookiebotOnAccept,
 * CookiebotOnConsentReady, `window.Cookiebot.consent.marketing`), the same pair
 * snippets/sk-consent.liquid already listens for GA4 Consent Mode. If Cookiebot never loads on
 * a page (script blocked, removed, still mid-rollout), these events simply never fire and this
 * file does nothing - no error, no fallback load, no Klaviyo. A decline needs no separate branch:
 * `loaded` starts false and only ever moves to true inside load(), so a marketing decline (via
 * either CookiebotOnDecline or a CookiebotOnAccept/OnConsentReady with marketing left off)
 * simply never calls it.
 */
(function () {
  var COMPANY_ID = window.SK_KLAVIYO_ID || 'T9zzX8';
  var loaded = false;

  function load() {
    if (loaded) return;
    if (document.querySelector('script[data-sk-klaviyo]')) { loaded = true; return; }
    loaded = true;
    var s = document.createElement('script');
    s.async = true;
    s.setAttribute('data-sk-klaviyo', '1');
    s.src = 'https://static.klaviyo.com/onsite/js/' + COMPANY_ID + '/klaviyo.js?company_id=' + COMPANY_ID;
    document.head.appendChild(s);
  }

  function marketingGranted() {
    return !!(window.Cookiebot && window.Cookiebot.consent && window.Cookiebot.consent.marketing);
  }

  function check() {
    if (marketingGranted()) load();
  }

  // Fires on first consent and on every later page load for a returning visitor who already
  // consented (Cookiebot's documented behaviour for CookiebotOnAccept). Covers the case where
  // the shopper only accepted some categories: check() re-reads marketing specifically rather
  // than assuming "accept" means every category.
  window.addEventListener('CookiebotOnAccept', check);

  // Fires once Cookiebot has resolved a consent state for this visitor by any means - banner
  // interaction just now, or a stored decision from an earlier visit - so a returning visitor
  // who accepted last time loads Klaviyo again without needing to re-click anything.
  window.addEventListener('CookiebotOnConsentReady', check);

  // Explicit no-op, kept for readability rather than because it changes behaviour: `loaded`
  // stays false, so a decline just means load() is never called by the two listeners above.
  // There is nothing here to revoke - if a page reaches this point, Klaviyo was never injected.
  window.addEventListener('CookiebotOnDecline', function () {});
})();
